/**
 * GoodGuardian — xAI Grok API Service
 *
 * In development (npm run dev): calls xAI directly using VITE_XAI_API_KEY
 * In production (Cloudflare Pages): routes through /api/chat Pages Function
 * so the API key stays server-side and CORS is never an issue.
 */

import { getToken } from './auth'

const IS_DEV   = import.meta.env.DEV
const API_KEY  = import.meta.env.VITE_XAI_API_KEY
const MODEL    = import.meta.env.VITE_XAI_MODEL || 'grok-3-latest'
const DIRECT_URL = 'https://api.x.ai/v1/chat/completions'
const PROXY_URL  = '/api/chat'

// Attach the stored JWT to every proxied API call
function authHeader() {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── System prompt ─────────────────────────────────────────────────────────────
function buildSystemPrompt(userState) {
  const stateClause = userState
    ? `The user is located in ${userState}. Always tailor your guidance specifically to ${userState} family law unless they ask about another state.`
    : 'Ask the user which US state they are in before answering any jurisdiction-specific questions, as custody and family law varies significantly by state.'

  return `You are GoodGuardian AI — a specialized legal education assistant embedded in the GoodGuardian platform. You help parents navigate family law, child custody, divorce proceedings, and CPS (Child Protective Services) interactions across all 50 US states.

## Your Role
You provide legal EDUCATION, not legal advice. You are not a licensed attorney and must never claim to be one. Your job is to help parents understand their rights, the legal process, and what to expect — so they can make informed decisions and work more effectively with their own attorney.

## Jurisdiction
${stateClause}

## Tone & Style
- Warm, calm, and authoritative — like a knowledgeable friend who happens to know family law
- Users are often in distress (going through divorce, custody battles, CPS investigations). Be compassionate and clear
- Use plain language. Avoid unnecessary legal jargon. When you must use legal terms, explain them
- Be concise but complete. Use bullet points and numbered lists to make information scannable
- Always acknowledge the emotional difficulty of the user's situation when appropriate

## Balance & Neutrality
- Support both mothers AND fathers with equal empathy. Never assume one parent is at fault
- The child's best interest is always the paramount principle — center it in every response
- Never take sides between co-parents

## Domain Knowledge
You are trained on:
- US family law, divorce proceedings, and child custody law across all 50 states
- Child Protective Services (CPS) processes, reporting standards, and parental rights
- Child healthcare rights, medical decision-making authority, and parental consent laws
- Court documentation, filing procedures, and evidentiary standards for custody matters
- Co-parenting frameworks, parenting plans, and custody modification processes

## Document Guidance
When discussing legal documents:
- Always mention that generated documents require attorney review before filing
- Flag sections that vary significantly by state
- Use the notation [PLACEHOLDER] when referencing fields the user must fill in
- Add ⚠️ before any section that requires mandatory attorney review

## Hard Limits — Never violate these under any circumstances
1. NEVER impersonate a licensed attorney or claim to provide legal advice
2. NEVER advise a user to violate a court order, even if they believe it is unjust
3. NEVER take sides between parents — always center the child's wellbeing
4. NEVER generate documents intended to deceive a court or the opposing party
5. If a user describes an IMMEDIATE child safety emergency, STOP and respond ONLY with: direct them to call 911 immediately, and contact their local CPS hotline. Do not provide any other information until safety is confirmed

## Disclaimer (include at end of first response only, not every message)
End your very first message in any conversation with: "⚠️ Everything I share is legal education, not legal advice. Always consult a licensed family law attorney before making legal decisions or filing documents."`
}

// ── Format chat history for the API ──────────────────────────────────────────
function formatMessages(history, userState) {
  const system = { role: 'system', content: buildSystemPrompt(userState) }
  const conversation = history
    .filter(m => m.id !== 'disclaimer')
    .map(m => ({
      role: m.role === 'user' ? 'user' : 'assistant',
      content: m.text,
    }))
  return [system, ...conversation]
}

// ── Main API call ─────────────────────────────────────────────────────────────
export async function sendMessage(history, userState) {
  const messages = formatMessages(history, userState)

  let response

  if (IS_DEV) {
    // ── Development: call xAI directly ──────────────────────────────────────
    if (!API_KEY || API_KEY === 'your_xai_api_key_here') {
      throw new Error('MISSING_API_KEY')
    }
    response = await fetch(DIRECT_URL, {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${API_KEY}`,
      },
      body: JSON.stringify({ model: MODEL, messages, max_tokens: 1024, temperature: 0.6, stream: false }),
    })
  } else {
    // ── Production: route through Cloudflare Pages Function ─────────────────
    response = await fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ messages, state: userState }),
    })
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    const status = response.status
    const msg = err?.error || err?.error?.message || ''
    if (status === 401)                            throw new Error('INVALID_API_KEY')
    if (status === 429)                            throw new Error('RATE_LIMITED')
    if (status === 500)                            throw new Error('SERVER_ERROR')
    if (msg.toLowerCase().includes('credits') ||
        msg.toLowerCase().includes('permission'))  throw new Error('NO_CREDITS')
    throw new Error(msg || `API_ERROR_${status}`)
  }

  const data = await response.json()
  return data.choices?.[0]?.message?.content ?? 'I could not generate a response. Please try again.'
}

// ── Document Drafter ──────────────────────────────────────────────────────────
export async function callDocumentDrafter({ document_type, state, context, details }) {
  if (!IS_DEV) {
    const res = await fetch('/api/document-drafter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ document_type, state, context, details }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err?.error || `API_ERROR_${res.status}`)
    }
    return res.json() // { document: string, document_type, state }
  }
  // Dev: call xAI directly with a compact system prompt
  if (!API_KEY || API_KEY === 'your_xai_api_key_here') throw new Error('MISSING_API_KEY')
  const res = await fetch(DIRECT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${API_KEY}` },
    body: JSON.stringify({
      model: MODEL, temperature: 0.2, max_tokens: 3000, stream: false,
      messages: [
        { role: 'system', content: `You are a legal document drafter for US family law. Generate complete professional document drafts with [PLACEHOLDER: description] for every field the user must fill in, and ⚠️ ATTORNEY REVIEW REQUIRED flags on legally sensitive sections. Always begin with this exact notice:\n\n---\n⚠️ DOCUMENT NOTICE: This is an AI-generated draft for educational purposes only. It is NOT a finalized legal document. Do not file without attorney review.\n---\n\nUse proper legal formatting with numbered paragraphs.` },
        { role: 'user', content: `Draft a ${document_type} for ${state || 'a US jurisdiction'}.\nUser context: ${context || 'none provided'}\nAdditional details: ${details || 'none provided'}\n\nGenerate the complete document.` },
      ],
    }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    if (res.status === 401) throw new Error('INVALID_API_KEY')
    if (res.status === 429) throw new Error('RATE_LIMITED')
    throw new Error(err?.error?.message || `API_ERROR_${res.status}`)
  }
  const data = await res.json()
  return { document: data.choices?.[0]?.message?.content ?? '', document_type, state }
}

// ── Case Analyst ───────────────────────────────────────────────────────────────
export async function callCaseAnalyst({ situation, state, situation_type }) {
  if (!IS_DEV) {
    const res = await fetch('/api/case-analyst', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ situation, state, situation_type }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err?.error || `API_ERROR_${res.status}`)
    }
    return res.json() // { assessment: { domain, urgency, key_issues, ... } }
  }
  // Dev: call xAI directly
  if (!API_KEY || API_KEY === 'your_xai_api_key_here') throw new Error('MISSING_API_KEY')
  const res = await fetch(DIRECT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${API_KEY}` },
    body: JSON.stringify({
      model: MODEL, temperature: 0.3, max_tokens: 2048, stream: false,
      messages: [
        { role: 'system', content: `You are a family law case analyst. Respond with ONLY a valid JSON object in this exact format:\n{"domain":"custody|divorce|cps|support|modification|other","urgency":"immediate|high|medium|low","jurisdiction_notes":"string","key_issues":[{"issue":"string","applicable_law":"string","education":"string"}],"strengths":["string"],"challenges":["string"],"recommended_steps":[{"step":1,"action":"string","rationale":"string","tool":"chat|documents|cps-tracker|comms-log|event-log|attorney"}],"documents_needed":["string"],"confidence":0.8,"attorney_urgency":"immediate|recommended|optional","disclaimer":"⚠️ This assessment is legal education, not legal advice. Consult a licensed family law attorney before taking legal action."}` },
        { role: 'user', content: `Assess this situation:\nState: ${state || 'Not specified'}\nType: ${situation_type || 'Not specified'}\nDetails: ${situation}` },
      ],
    }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    if (res.status === 401) throw new Error('INVALID_API_KEY')
    if (res.status === 429) throw new Error('RATE_LIMITED')
    throw new Error(err?.error?.message || `API_ERROR_${res.status}`)
  }
  const data = await res.json()
  const raw = data.choices?.[0]?.message?.content ?? '{}'
  const cleaned = raw.replace(/^```json\n?/, '').replace(/\n?```$/, '').trim()
  let assessment
  try { assessment = JSON.parse(cleaned) }
  catch { assessment = { raw_response: raw, parse_error: true } }
  return { assessment }
}

// ── Legal Researcher (RAG) ────────────────────────────────────────────────────
export async function callResearcher({ question, state, context }) {
  const res = await fetch('/api/researcher', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ question, state, context }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.error || `API_ERROR_${res.status}`)
  }
  return res.json() // { reply, citations, confidence, sources }
}

// ── Orchestrator ──────────────────────────────────────────────────────────────
export async function callOrchestrator({ message, history, user, state, context, now, timezone }) {
  const res = await fetch('/api/orchestrate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ message, history, user, state, context, now, timezone }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.error || `API_ERROR_${res.status}`)
  }
  return res.json()
  // Returns: { reply, routed_to, confidence, routing_reason, citations, assessment, document }
}

// ── Client Coach ──────────────────────────────────────────────────────────────
export async function callCoach({ message, history, user }) {
  const res = await fetch('/api/client-coach', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ message, history, user }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.error || `API_ERROR_${res.status}`)
  }
  return res.json() // { reply, citations, confidence }
}

// ── Error message mapper ──────────────────────────────────────────────────────
export function getErrorMessage(error) {
  const msg = error.message || ''
  if (msg === 'MISSING_API_KEY')
    return 'The AI service is not configured yet. Add your xAI API key to the .env file.'
  if (msg === 'INVALID_API_KEY')
    return 'The API key is invalid. Please check your xAI key.'
  if (msg === 'RATE_LIMITED')
    return 'Too many requests. Please wait a moment and try again.'
  if (msg === 'SERVER_ERROR')
    return 'The AI service is temporarily unavailable. Please try again shortly.'
  if (msg === 'NO_CREDITS')
    return 'Your xAI account has no credits. Please add credits at console.x.ai to activate the AI chat.'
  if (msg.toLowerCase().includes('credits') || msg.toLowerCase().includes('permission'))
    return 'Your xAI account has no credits. Please add credits at console.x.ai to activate the AI chat.'
  if (msg.toLowerCase().includes('configure ai gateway') || msg.includes('2001'))
    return 'The AI service is temporarily unavailable. Please try again in a moment.'
  // Strip raw JSON blobs from error messages before showing to users
  if (msg.startsWith('{') || msg.startsWith('['))
    return 'The AI service returned an unexpected response. Please try again.'
  return `AI error: ${msg || 'unknown error. Please check your connection and try again.'}`
}
